import { Fragment, useState } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Button, SlashLabel } from "@/components/primitives";
import { RadarPlot } from "@/components/Plots";
import {
  TEST_QUEUE,
  TEST_FAMILY_META,
  STATUS_TONE,
  HEDONIC_DISTRIBUTION,
  JAR_ATTRIBUTES,
  PI_BOXES,
  QDA_AXES,
  QDA_TARGET,
  QDA_PANEL,
  QDA_PREDICTED,
  QDA_PVALUES,
  PANELISTS,
  QC_CHECKS,
  DFC_DEFAULT_ROWS,
  TEMPORAL_METRICS,
  type SensoryTest,
  type TestFamily,
  type TestStatus,
} from "@/services/mock";

type AssessPhaseProps = {
  variantName: string;
};

export function AssessPhase({ variantName }: AssessPhaseProps) {
  void variantName;
  const [activeId, setActiveId] = useState("t-cons-r2");
  const test = TEST_QUEUE.find((t) => t.id === activeId) || TEST_QUEUE[0];

  const renderBoard = () => {
    if (test.status === "scheduled") return <ScheduledBoard test={test} />;
    switch (test.family) {
      case "discrimination":
        if (test.method.toLowerCase().includes("difference-from-control")) {
          return <DFCBoard test={test} />;
        }
        return <TriangleBoard test={test} />;
      case "descriptive":
        return <DescriptiveBoard />;
      case "consumer":
        return <ConsumerBoard />;
      case "temporal":
        return <TemporalBoard />;
      default:
        return <ScheduledBoard test={test} />;
    }
  };

  return (
    <div className="assess-grid fade-in">
      <TestQueue tests={TEST_QUEUE} activeId={activeId} onSelect={setActiveId} />

      <div style={{ minHeight: 0, minWidth: 0, overflow: "auto" }}>
        <div className="vstack gap-3">
          <VerdictHeader test={test} />
          {renderBoard()}
        </div>
      </div>

      <div style={{ minHeight: 0, minWidth: 0 }}>
        <DecisionRail />
      </div>
    </div>
  );
}

// ───────────── Test Queue ─────────────

function StatusChip({ status }: { status: TestStatus }) {
  const t = STATUS_TONE[status];
  return (
    <span
      className="status-chip"
      style={{ background: t.bg, borderColor: t.line, color: t.fg }}
    >
      {t.lbl}
    </span>
  );
}

type TestQueueProps = {
  tests: SensoryTest[];
  activeId: string;
  onSelect: (id: string) => void;
};

function TestQueue({ tests, activeId, onSelect }: TestQueueProps) {
  const families = Object.entries(TEST_FAMILY_META) as [
    TestFamily,
    (typeof TEST_FAMILY_META)[TestFamily]
  ][];

  return (
    <div className="panel test-queue">
      <div className="test-queue-head">
        <div className="hstack gap-2">
          <Icon name="target" size={14} />
          <span className="label">Test queue</span>
        </div>
        <button
          type="button"
          className="btn xs ghost"
          title="Design a new sensory test"
        >
          <Icon name="plus" size={11} />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.06em",
            }}
          >
            NEW
          </span>
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {families.map(([famId, fam]) => {
          const list = tests.filter((t) => t.family === famId);
          if (list.length === 0) return null;
          return (
            <Fragment key={famId}>
              <div className="test-queue-fam-head">
                <span>
                  <span style={{ color: fam.tone }}>■</span> {fam.label}
                </span>
                <span>{list.length}</span>
              </div>
              {list.map((t) => {
                const active = t.id === activeId;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onSelect(t.id)}
                    className={cn("test-queue-row", active && "active")}
                    style={{
                      borderLeftColor: active ? fam.tone : "transparent",
                    }}
                  >
                    <div
                      className="hstack"
                      style={{ justifyContent: "space-between", marginBottom: 4 }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: "var(--ink)",
                          fontWeight: active ? 600 : 500,
                        }}
                      >
                        {t.method}
                      </span>
                      <StatusChip status={t.status} />
                    </div>
                    <div
                      style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 4 }}
                    >
                      {t.title}
                    </div>
                    <div
                      className="hstack mono"
                      style={{
                        gap: 10,
                        fontSize: 9.5,
                        color: "var(--ink-4)",
                        letterSpacing: "0.04em",
                      }}
                    >
                      <span>n={t.panel}</span>
                      {t.pValue != null && <span>p={t.pValue.toFixed(3)}</span>}
                      {t.dprime != null && <span>d′={t.dprime.toFixed(2)}</span>}
                      <span style={{ marginLeft: "auto" }}>{t.when}</span>
                    </div>
                  </button>
                );
              })}
            </Fragment>
          );
        })}
      </div>

      <div className="test-queue-footer">
        <div
          className="hstack mono"
          style={{
            justifyContent: "space-between",
            fontSize: 9.5,
            color: "var(--ink-4)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          <span>Project</span>
          <span>R2 / 4 rounds</span>
        </div>
        <div className="test-queue-progress">
          <div className="test-queue-progress-fill" style={{ width: "55%" }} />
        </div>
      </div>
    </div>
  );
}

// ───────────── Verdict Header ─────────────

function VerdictHeader({ test }: { test: SensoryTest }) {
  const f = TEST_FAMILY_META[test.family];

  let primary: string;
  let secondary: string;
  let tone: string;
  let hint: string;

  if (test.family === "discrimination") {
    if (test.method.toLowerCase().includes("difference-from-control")) {
      primary = `Mean DFC ${
        test.overall != null ? test.overall.toFixed(1) : "—"
      } / 10 · moderate departure from gold`;
      secondary = `n = ${test.panel} · α = ${test.alpha} · blind reference baseline 0.4`;
      tone = "var(--brand-amber)";
      hint =
        "Aroma & aftertaste drive the gap; mouthfeel reads on-spec. See attribute breakdown.";
    } else if (test.verdict === "different") {
      primary = "Different from reference";
      secondary = `p = ${test.pValue?.toFixed(3) ?? "—"} · d′ = ${test.dprime?.toFixed(2) ?? "—"} · ${test.correct}/${test.panel} correct`;
      tone = "var(--brand-amber)";
      hint =
        "Triangle test rejects equivalence. Inspect DFC for direction & magnitude.";
    } else {
      primary = "Indistinguishable from reference";
      secondary = `p = ${test.pValue?.toFixed(3) ?? "—"} · ${test.correct}/${test.panel} correct`;
      tone = "var(--brand-green)";
      hint =
        "Cannot reject H₀ at α=0.05. Equivalence test recommended before claiming match.";
    }
  } else if (test.family === "descriptive") {
    primary = "Trained-panel profile · 11 panelists × 3 reps";
    secondary =
      "Two-way ANOVA per attribute · panelist × sample. PCA on attribute matrix.";
    tone = "var(--brand-green)";
    hint =
      "Panel performance audited (PanelCheck): all panelists within consistency bounds.";
  } else if (test.family === "consumer") {
    primary = "Top-2-box PI 58% · mean liking 7.3 / 9";
    secondary =
      "vs. incumbent: +11 pts PI · +0.4 liking · n=148 · category users";
    tone = "var(--brand-green)";
    hint =
      "Penalty analysis flags vanilla intensity slightly low (-0.4 on liking, 28% of consumers).";
  } else if (test.family === "temporal") {
    primary = "Dominance curves — sweetness 0–18 s, bitterness onset 6 s";
    secondary = "TDS · 9 trained panelists · 6 attributes";
    tone = "var(--fam-citrus)";
    hint = "Bitter persistence drops 38% vs. v1 — masking confirmed.";
  } else {
    primary = "Threshold not yet established";
    secondary = "Ascending 3-AFC · BET geometric mean across panel";
    tone = "var(--ink-3)";
    hint = "Schedule run to establish operating concentration.";
  }

  return (
    <div className="panel verdict-panel">
      <div
        className="hstack"
        style={{ justifyContent: "space-between", marginBottom: 12 }}
      >
        <div className="hstack gap-2">
          <span
            style={{ width: 8, height: 8, background: f.tone, borderRadius: 1 }}
          />
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-3)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {f.label} · {test.method}
          </span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            · {test.id}
          </span>
        </div>
        <div className="hstack gap-2">
          <StatusChip status={test.status} />
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            {test.when}
          </span>
        </div>
      </div>
      <div className="hstack" style={{ alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="verdict-primary">{primary}</div>
          <div className="verdict-secondary">{secondary}</div>
          <div
            className="verdict-hint"
            style={{ borderLeft: `2px solid ${tone}` }}
          >
            {hint}
          </div>
        </div>
        <div className="vstack gap-2" style={{ flexShrink: 0 }}>
          <Button size="sm" variant="primary">
            <Icon name="check" size={12} /> Sign off round
          </Button>
          <Button size="sm">
            <Icon name="download" size={12} /> Client report
          </Button>
          <Button size="sm" variant="ghost">
            <Icon name="external" size={12} /> Open ballot
          </Button>
        </div>
      </div>
    </div>
  );
}

// ───────────── Boards ─────────────

function ConsumerBoard() {
  const total = HEDONIC_DISTRIBUTION.reduce((s, v) => s + v, 0);
  const mean =
    HEDONIC_DISTRIBUTION.reduce((s, v, i) => s + v * (i + 1), 0) / total;
  const t2b =
    Math.round(
      ((HEDONIC_DISTRIBUTION[7] + HEDONIC_DISTRIBUTION[8]) / total) * 100
    );

  return (
    <div className="vstack gap-3">
      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 10 }}
        >
          <span className="label">9-pt hedonic distribution · n=148</span>
          <div className="hstack gap-3">
            <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-2)" }}>
              mean <b style={{ color: "var(--ink)" }}>{mean.toFixed(2)}</b>
            </span>
            <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-2)" }}>
              top-2-box <b style={{ color: "var(--brand-green)" }}>{t2b}%</b>
            </span>
            <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-2)" }}>
              σ <b style={{ color: "var(--ink)" }}>1.42</b>
            </span>
          </div>
        </div>
        <Histogram counts={HEDONIC_DISTRIBUTION} height={120} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="panel assess-board-panel">
          <div className="label" style={{ marginBottom: 12 }}>
            JAR · penalty analysis
          </div>
          <div className="vstack gap-3">
            {JAR_ATTRIBUTES.map((a) => (
              <JARBar key={a.name} attr={a} />
            ))}
          </div>
        </div>

        <div className="panel assess-board-panel">
          <div
            className="hstack"
            style={{ justifyContent: "space-between", marginBottom: 10 }}
          >
            <span className="label">Purchase intent · 5-pt Juster</span>
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--brand-green)", fontWeight: 600 }}
            >
              T2B 58%
            </span>
          </div>
          <div className="vstack gap-2">
            {PI_BOXES.map((b) => (
              <div key={b.lbl}>
                <div
                  className="hstack"
                  style={{
                    justifyContent: "space-between",
                    fontSize: 11,
                    marginBottom: 3,
                  }}
                >
                  <span style={{ color: "var(--ink-2)", textTransform: "capitalize" }}>
                    {b.lbl}
                  </span>
                  <span className="mono" style={{ color: "var(--ink-3)" }}>
                    {b.pct}%
                  </span>
                </div>
                <div className="pi-bar">
                  <div
                    style={{
                      width: `${b.pct * 2}%`,
                      height: "100%",
                      background: b.tone,
                      opacity: 0.85,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Histogram({ counts, height = 110 }: { counts: number[]; height?: number }) {
  const max = Math.max(...counts, 1);
  const labels = counts.map((_, i) => String(i + 1));
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${counts.length * 28} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
    >
      {counts.map((c, i) => {
        const h = (c / max) * (height - 22);
        return (
          <g key={i}>
            <rect
              x={i * 28 + 4}
              y={height - 18 - h}
              width={20}
              height={h}
              fill="var(--brand-green)"
              fillOpacity={0.85}
              rx="1"
            />
            <text
              x={i * 28 + 14}
              y={height - 6}
              textAnchor="middle"
              fontSize="9"
              fontFamily="var(--font-mono)"
              fill="var(--ink-3)"
            >
              {labels[i]}
            </text>
            <text
              x={i * 28 + 14}
              y={height - 22 - h - 2}
              textAnchor="middle"
              fontSize="9"
              fontFamily="var(--font-mono)"
              fill="var(--ink-4)"
            >
              {c || ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function JARBar({
  attr,
}: {
  attr: { name: string; dist: [number, number, number, number, number]; penalty: number };
}) {
  const tones = ["#c92619", "#e88a3a", "var(--brand-green)", "#e88a3a", "#c92619"];
  const total = attr.dist.reduce((s, v) => s + v, 0) || 1;
  const labels = [
    "much too low",
    "slightly too low",
    "JAR",
    "slightly too high",
    "much too high",
  ];
  return (
    <div>
      <div
        className="hstack"
        style={{ justifyContent: "space-between", fontSize: 11.5, marginBottom: 4 }}
      >
        <span style={{ color: "var(--ink-2)" }}>{attr.name}</span>
        <span
          className="mono"
          style={{
            fontSize: 10.5,
            color: attr.penalty < -0.3 ? "#c92619" : "var(--ink-3)",
          }}
        >
          penalty {attr.penalty > 0 ? "+" : ""}
          {attr.penalty.toFixed(2)} on liking
        </span>
      </div>
      <div className="jar-bar">
        {attr.dist.map((v, i) => (
          <div
            key={i}
            title={`${labels[i]}: ${Math.round((v / total) * 100)}%`}
            style={{
              width: `${(v / total) * 100}%`,
              height: "100%",
              background: tones[i],
              opacity: i === 2 ? 1 : 0.6,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function DescriptiveBoard() {
  const rows = QDA_AXES.map((name, i) => ({
    name,
    target: QDA_TARGET[i],
    panel: QDA_PANEL[i],
    predicted: QDA_PREDICTED[i],
    delta: QDA_PANEL[i] - QDA_TARGET[i],
    p: QDA_PVALUES[i],
  }));

  return (
    <div className="vstack gap-3">
      <div className="panel assess-board-panel">
        <RadarPlot
          axes={QDA_AXES}
          target={QDA_TARGET}
          current={QDA_PANEL}
          size={340}
          label="QDA · target vs panel · 9 axes"
        />
      </div>

      <div className="panel" style={{ background: "var(--bg-elev)", overflow: "hidden" }}>
        <div
          className="hstack"
          style={{ padding: "12px 14px", justifyContent: "space-between" }}
        >
          <span className="label">Attribute table · panel vs target vs model</span>
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: "var(--ink-4)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            two-way ANOVA · α=0.05
          </span>
        </div>
        <table className="data-table" style={{ fontSize: 11.5 }}>
          <thead>
            <tr>
              <th>Attribute</th>
              <th style={{ textAlign: "right" }}>Target</th>
              <th style={{ textAlign: "right" }}>Panel</th>
              <th style={{ textAlign: "right" }}>Δ</th>
              <th style={{ textAlign: "right" }}>Predicted</th>
              <th style={{ textAlign: "right" }}>p</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const sig = r.p < 0.05;
              const big = Math.abs(r.delta) > 0.8;
              return (
                <tr key={r.name}>
                  <td
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: "0.04em",
                      color: "var(--ink-2)",
                    }}
                  >
                    {r.name}
                  </td>
                  <td className="mono" style={{ textAlign: "right", color: "var(--ink-3)" }}>
                    {r.target.toFixed(1)}
                  </td>
                  <td
                    className="mono"
                    style={{
                      textAlign: "right",
                      color: "var(--ink)",
                      fontWeight: 600,
                    }}
                  >
                    {r.panel.toFixed(1)}
                  </td>
                  <td
                    className="mono"
                    style={{
                      textAlign: "right",
                      color: big ? "var(--brand-amber)" : "var(--ink-3)",
                    }}
                  >
                    {r.delta > 0 ? "+" : ""}
                    {r.delta.toFixed(1)}
                  </td>
                  <td
                    className="mono"
                    style={{
                      textAlign: "right",
                      color: "var(--brand-amber)",
                      opacity: 0.85,
                    }}
                  >
                    {r.predicted.toFixed(1)}
                  </td>
                  <td
                    className="mono"
                    style={{
                      textAlign: "right",
                      color: sig ? "#c25a4a" : "var(--ink-3)",
                      fontWeight: sig ? 600 : 400,
                    }}
                  >
                    {r.p.toFixed(2)}
                  </td>
                  <td>
                    <span className={cn("attr-verdict", sig ? "off" : "match")}>
                      {sig ? "OFF-TARGET" : "MATCH"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DFCBoard({ test }: { test: SensoryTest }) {
  const byAttr = test.byAttr || {};
  const rows = [
    { name: "Aroma", v: byAttr.aroma ?? 3.1, blind: 0.4 },
    { name: "Flavor", v: byAttr.flavor ?? 2.4, blind: 0.5 },
    { name: "Mouthfeel", v: byAttr.mouthfeel ?? 1.2, blind: 0.3 },
    { name: "Aftertaste", v: byAttr.aftertaste ?? 3.0, blind: 0.4 },
    { name: "Overall", v: test.overall ?? 2.6, blind: 0.4, bold: true },
  ];

  return (
    <div className="vstack gap-3">
      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 12 }}
        >
          <span className="label">
            Difference-from-control · attribute breakdown
          </span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            0 = blind reference · 10 = extreme
          </span>
        </div>
        <div className="vstack gap-3">
          {rows.map((r) => (
            <div key={r.name}>
              <div
                className="hstack"
                style={{
                  justifyContent: "space-between",
                  fontSize: 11.5,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    color: "var(--ink-2)",
                    fontWeight: r.bold ? 600 : 500,
                  }}
                >
                  {r.name}
                </span>
                <span
                  className="mono"
                  style={{
                    color: "var(--ink)",
                    fontWeight: r.bold ? 700 : 500,
                  }}
                >
                  {r.v.toFixed(1)}{" "}
                  <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
                    (blind R: {r.blind.toFixed(1)})
                  </span>
                </span>
              </div>
              <div className="dfc-row-bg">
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${r.v * 10}%`,
                    background:
                      r.v > 2.0 ? "var(--brand-amber)" : "var(--brand-green)",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: `${r.blind * 10}%`,
                    top: -2,
                    bottom: -2,
                    width: 2,
                    background: "var(--ink-2)",
                    borderRadius: 1,
                  }}
                  title="blind reference baseline"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="panel assess-board-panel">
        <div className="label" style={{ marginBottom: 10 }}>
          Test design
        </div>
        <div className="vstack gap-2">
          <DesignRow lbl="Method" val="Difference-from-control · 0–10 unstructured" />
          <DesignRow lbl="Panel" val={`n = ${test.panel} · trained`} />
          <DesignRow lbl="α" val={String(test.alpha ?? 0.05)} />
          <DesignRow
            lbl="Blind ref"
            val="Identical control served as test (anchor for noise floor)"
          />
        </div>
      </div>
    </div>
  );
}

function TriangleBoard({ test }: { test: SensoryTest }) {
  const N = test.panel || 36;
  const K = test.correct || 21;
  const critical = Math.ceil(0.444 * N);

  // Binomial(N, 1/3) pmf
  const choose = (n: number, k: number) => {
    let r = 1;
    for (let i = 1; i <= k; i++) r = (r * (n - i + 1)) / i;
    return r;
  };
  const dist: number[] = [];
  for (let k = 0; k <= N; k++) {
    dist.push(choose(N, k) * Math.pow(1 / 3, k) * Math.pow(2 / 3, N - k));
  }
  const maxD = Math.max(...dist);

  return (
    <div className="vstack gap-3">
      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 8 }}
        >
          <span className="label">Binomial null · H₀: random guessing (p=⅓)</span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            n={N} · α=0.05 · critical ≥ {critical}
          </span>
        </div>
        <svg
          width="100%"
          viewBox={`0 0 ${(N + 1) * 14} 130`}
          style={{ display: "block" }}
        >
          {dist.map((p, k) => {
            const h = (p / maxD) * 100;
            const isObs = k === K;
            const isReject = k >= critical;
            return (
              <g key={k}>
                <rect
                  x={k * 14 + 2}
                  y={120 - h}
                  width={10}
                  height={h}
                  fill={
                    isObs
                      ? "var(--accent)"
                      : isReject
                      ? "var(--brand-amber)"
                      : "var(--ink-4)"
                  }
                  fillOpacity={isObs ? 1 : isReject ? 0.45 : 0.3}
                />
                {(k % 5 === 0 || isObs) && (
                  <text
                    x={k * 14 + 7}
                    y={130}
                    textAnchor="middle"
                    fontSize="8"
                    fontFamily="var(--font-mono)"
                    fill={isObs ? "var(--accent-ink)" : "var(--ink-4)"}
                    fontWeight={isObs ? 700 : 400}
                  >
                    {k}
                  </text>
                )}
              </g>
            );
          })}
          <line
            x1={K * 14 + 7}
            x2={K * 14 + 7}
            y1={6}
            y2={120}
            stroke="var(--accent-ink)"
            strokeWidth="1"
            strokeDasharray="2 2"
          />
          <text
            x={K * 14 + 12}
            y={14}
            fontSize="9.5"
            fontFamily="var(--font-mono)"
            fill="var(--accent-ink)"
            fontWeight={600}
          >
            observed {K}
          </text>
        </svg>
        <div className="hstack" style={{ marginTop: 10, gap: 18 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
            p = <b>{test.pValue?.toFixed(3) ?? "—"}</b>
          </span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
            Thurstonian d′ = <b>{test.dprime?.toFixed(2) ?? "—"}</b>
          </span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
            power(1-β) = <b>0.82</b>
          </span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {test.served}
          </span>
        </div>
      </div>

      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 12 }}
        >
          <span className="label">Difference-from-control · attribute breakdown</span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            0 = blind reference · 10 = extreme
          </span>
        </div>
        <div className="vstack gap-3">
          {DFC_DEFAULT_ROWS.map((r) => (
            <div key={r.name}>
              <div
                className="hstack"
                style={{
                  justifyContent: "space-between",
                  fontSize: 11.5,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    color: "var(--ink-2)",
                    fontWeight: r.bold ? 600 : 500,
                  }}
                >
                  {r.name}
                </span>
                <span
                  className="mono"
                  style={{
                    color: "var(--ink)",
                    fontWeight: r.bold ? 700 : 500,
                  }}
                >
                  {r.v.toFixed(1)}{" "}
                  <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
                    (blind R: {r.blind.toFixed(1)})
                  </span>
                </span>
              </div>
              <div className="dfc-row-bg">
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${r.v * 10}%`,
                    background:
                      r.v > 2.0 ? "var(--brand-amber)" : "var(--brand-green)",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: `${r.blind * 10}%`,
                    top: -2,
                    bottom: -2,
                    width: 2,
                    background: "var(--ink-2)",
                    borderRadius: 1,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TemporalBoard() {
  return (
    <div className="vstack gap-3">
      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 8 }}
        >
          <span className="label">
            Temporal Dominance of Sensations · 9 panelists × 6 attributes
          </span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            scheduled — preview metrics below
          </span>
        </div>
        <div
          style={{
            height: 180,
            border: "1px dashed var(--line-strong)",
            borderRadius: 6,
            background: "var(--bg-sunk)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--ink-4)",
            fontSize: 12,
          }}
        >
          TDS curves render once panel data lands
        </div>
      </div>
      <div className="panel assess-board-panel">
        <div className="label" style={{ marginBottom: 10 }}>
          Temporal metrics · per attribute
        </div>
        <table className="data-table" style={{ fontSize: 11.5 }}>
          <thead>
            <tr>
              <th>Attribute</th>
              <th style={{ textAlign: "right" }}>Time-to-peak</th>
              <th style={{ textAlign: "right" }}>Peak %</th>
              <th style={{ textAlign: "right" }}>Duration</th>
              <th style={{ textAlign: "right" }}>AUC</th>
              <th style={{ textAlign: "right" }}>Δ vs v1</th>
            </tr>
          </thead>
          <tbody>
            {TEMPORAL_METRICS.map((r) => (
              <tr key={r.n}>
                <td style={{ color: "var(--ink-2)" }}>{r.n}</td>
                <td className="mono" style={{ textAlign: "right", color: "var(--ink-2)" }}>
                  {r.tp}
                </td>
                <td className="mono" style={{ textAlign: "right", color: "var(--ink-2)" }}>
                  {r.pk}
                </td>
                <td className="mono" style={{ textAlign: "right", color: "var(--ink-2)" }}>
                  {r.dur}
                </td>
                <td className="mono" style={{ textAlign: "right", color: "var(--ink)" }}>
                  {r.auc.toFixed(1)}
                </td>
                <td
                  className="mono"
                  style={{
                    textAlign: "right",
                    color:
                      r.d < 0
                        ? "var(--brand-green)"
                        : r.d > 0
                        ? "var(--brand-amber)"
                        : "var(--ink-4)",
                  }}
                >
                  {r.d === 0 ? "—" : `${r.d > 0 ? "+" : ""}${r.d}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScheduledBoard({ test }: { test: SensoryTest }) {
  return (
    <div className="panel assess-board-panel" style={{ padding: 24 }}>
      <div className="vstack gap-3" style={{ alignItems: "flex-start" }}>
        <div className="hstack gap-2">
          <Icon name="history" size={14} />
          <span className="label">Scheduled · not yet run</span>
        </div>
        <div className="display" style={{ fontSize: 20, color: "var(--ink)" }}>
          {test.method} · {test.title}
        </div>
        <div className="vstack gap-2" style={{ width: "100%" }}>
          <DesignRow lbl="Test family" val={TEST_FAMILY_META[test.family].label} />
          <DesignRow
            lbl="Panel size"
            val={`n = ${test.panel} · ${test.family === "consumer" ? "category users" : "trained panel"}`}
          />
          <DesignRow lbl="Date" val={test.when} />
          <DesignRow lbl="α / β" val="0.05 / 0.20" />
          <DesignRow lbl="Randomization" val="Williams Latin square · 3-digit blinding" />
          <DesignRow lbl="Power" val="detectable d′ = 1.0 at chosen panel size" />
        </div>
        <div className="hstack gap-2">
          <Button size="sm" variant="primary">
            <Icon name="check" size={12} /> Confirm protocol
          </Button>
          <Button size="sm">
            <Icon name="edit" size={12} /> Edit design
          </Button>
        </div>
      </div>
    </div>
  );
}

function DesignRow({ lbl, val }: { lbl: string; val: string }) {
  return (
    <div className="design-row">
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--ink-4)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        {lbl}
      </span>
      <span style={{ fontSize: 12, color: "var(--ink-2)" }}>{val}</span>
    </div>
  );
}

// ───────────── Decision rail ─────────────

function DecisionRail() {
  return (
    <div className="decision-rail">
      <div className="dark-panel" style={{ padding: 14, borderRadius: 8 }}>
        <SlashLabel>Recommendation</SlashLabel>
        <div
          className="display"
          style={{ fontSize: 16, color: "#fff", lineHeight: 1.25, margin: "8px 0" }}
        >
          v2 beats incumbent on liking (p=0.02) — submit pending −0.4 vanilla
          intensity tweak
        </div>
        <div className="vstack gap-2" style={{ marginTop: 8 }}>
          <RecRow
            lbl="Match decision"
            val="Distinguishable from gold (Δ=2.6 DFC)"
            tone="amber"
          />
          <RecRow lbl="Top-2-box PI" val="58% vs 47% incumbent" tone="green" />
          <RecRow lbl="Liking lift" val="+0.4 (CI ±0.18)" tone="green" />
          <RecRow
            lbl="Drag attribute"
            val="Vanilla intensity (penalty −0.41)"
            tone="amber"
          />
          <RecRow
            lbl="Segment fit"
            val="2 clusters · 64% / 36% acceptance"
            tone="neutral"
          />
        </div>
      </div>

      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 8 }}
        >
          <span className="label">Molecular ↔ sensory loop</span>
          <span className="chip mono accent">PLS · GNN</span>
        </div>
        <div className="vstack gap-2">
          <KV lbl="Predicted profile MAE" val="0.72 / 15" tone="ok" />
          <KV lbl="Worst attribute miss" val="phenolic (Δ 0.7)" tone="warn" />
          <KV
            lbl="Training rows added"
            val="+11 panelists × 3 reps × 2 samples = 66"
          />
          <KV lbl="Loop generation" val="model v0.42 · last retrain 6 d ago" />
        </div>
        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            background: "var(--bg-sunk)",
            border: "1px dashed var(--line-strong)",
            borderRadius: 4,
            fontSize: 11.5,
            color: "var(--ink-2)",
          }}
        >
          <span
            style={{
              color: "var(--accent-ink)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginRight: 6,
            }}
          >
            NEXT
          </span>
          Model proposes 3 candidate molecules to lift vanilla character — review in
          Design.
        </div>
      </div>

      <div className="panel assess-board-panel">
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginBottom: 10 }}
        >
          <span className="label">Panelist performance · n=6</span>
          <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            discr · consist · agree
          </span>
        </div>
        <div className="vstack gap-2">
          {PANELISTS.map((p) => (
            <div key={p.name} className="panelist-row">
              <div className="panelist-avatar">
                {p.name
                  .split(" ")
                  .map((s) => s[0])
                  .join("")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontSize: 11.5, color: "var(--ink)", fontWeight: 500 }}
                >
                  {p.name}
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 9,
                    color: "var(--ink-4)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {p.role}
                </div>
              </div>
              <PerfBar v={p.d} />
              <PerfBar v={p.c} />
              <PerfBar v={p.a} />
              {p.status === "drift" && (
                <span
                  title="Drift flagged"
                  style={{ marginLeft: 4, color: "var(--brand-amber)" }}
                >
                  <Icon name="alert" size={11} />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="panel assess-board-panel">
        <div className="label" style={{ marginBottom: 10 }}>
          QC & regulatory
        </div>
        <div className="vstack gap-2">
          {QC_CHECKS.map((c) => {
            const lbl =
              c.status === "pass"
                ? "PASS"
                : c.status === "warn"
                ? "REVIEW"
                : "PENDING";
            const tone =
              c.status === "warn"
                ? { bg: "var(--signal-soft)", line: "#e7c97a", fg: "#7c5813" }
                : c.status === "pending"
                ? STATUS_TONE.scheduled
                : STATUS_TONE.complete;
            return (
              <div key={c.name} className="qc-row">
                <span style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
                  {c.name}
                </span>
                <span
                  className="qc-badge"
                  style={{
                    background: tone.bg,
                    borderColor: tone.line,
                    color: tone.fg,
                  }}
                >
                  {lbl}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PerfBar({ v }: { v: number }) {
  const pct = Math.max(0, Math.min(1, v));
  const color =
    pct > 0.85
      ? "var(--brand-green)"
      : pct > 0.75
      ? "var(--accent)"
      : "var(--brand-amber)";
  return (
    <div className="perf-bar" title={`${(pct * 100).toFixed(0)}%`}>
      <div style={{ width: `${pct * 100}%`, height: "100%", background: color }} />
    </div>
  );
}

function KV({
  lbl,
  val,
  tone,
}: {
  lbl: string;
  val: string;
  tone?: "ok" | "warn";
}) {
  const fg =
    tone === "ok" ? "var(--brand-green)" : tone === "warn" ? "var(--brand-amber)" : "var(--ink)";
  return (
    <div className="kv-row">
      <span style={{ color: "var(--ink-3)" }}>{lbl}</span>
      <span className="mono" style={{ color: fg, fontWeight: 600 }}>
        {val}
      </span>
    </div>
  );
}

function RecRow({
  lbl,
  val,
  tone,
}: {
  lbl: string;
  val: string;
  tone: "green" | "amber" | "neutral";
}) {
  const dot =
    tone === "green"
      ? "var(--brand-lime)"
      : tone === "amber"
      ? "var(--brand-amber)"
      : "#9aa89a";
  return (
    <div className="decision-rec-row">
      <span
        style={{
          color: "#9aa89a",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{ width: 6, height: 6, borderRadius: 1, background: dot }}
        />
        {lbl}
      </span>
      <span className="mono" style={{ color: "#ecebe1" }}>
        {val}
      </span>
    </div>
  );
}
